import { useAppSelector, useAppDispatch } from '@doda/pc/src/redux/hooks';
import type { AxiosError } from 'axios';
import { apiClient } from '@doda/api-client/util/apiClient';
import type {
  ErrorResponseBody,
  ScoutDivisionCode,
} from '@doda/api-client/aspida/@types';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import type { BookmarkProcessState } from '@doda/common/src/hooks/useSaveBookmark';
import { AppThunk } from '@doda/pc/src/redux/store';
import {
  bookmarkStateSelector,
  setBookmarkState,
} from '../../redux/slices/bookmark';

/** 引数型 */
export interface Arg {
  jid: string;
  isBookmarked: boolean;
  /** 法人窓口ID */
  wapid: string;
  /** 気になる成功時の処理 */
  onSuccess?: VoidFunction;
}

/**
 * 気になるロジック(求人詳細)
 * @param root0
 */
export function useBookmark({ jid, isBookmarked, wapid, onSuccess }: Arg) {
  const dispatch = useAppDispatch();
  const bookmarkStateOnClient = useAppSelector(bookmarkStateSelector);
  const bookmarkState: BookmarkProcessState = isBookmarked
    ? 'saved'
    : bookmarkStateOnClient;

  const saveBookmark = async () => {
    dispatch(setBookmarkState('loading'));
    try {
      await dispatch(bookmark(jid, wapid));
      onSuccess?.();
      dispatch(setBookmarkState('saved'));
    } catch (err) {
      const error = err as AxiosError<ErrorResponseBody>;
      window.DD_LOGS?.logger.info(
        `気になる登録に失敗 jid: ${jid} エラーメッセージ: ${error.response?.data.message || error}`
      );
      dispatch(setBookmarkState('default'));
    }
  };

  return { bookmarkState, saveBookmark };
}

/**
 * 気になるAPI処理
 * @param jid
 * @param wapid
 */
function bookmark(jid: string, wapid: string): AppThunk<Promise<void>> {
  return (_, getState) => {
    const {
      osscfl,
      scoutlist_id,
      message_id,
      record_scoutlist_id,
      record_message_id,
    } = appQueryParamsSelector(getState());
    return apiClient.api.v1.job._jid(jid).bookmark.$post({
      body: {
        device: 'PC',
        corporateReceptionId: wapid,
        scoutDivisionCode: (osscfl as ScoutDivisionCode) || undefined,
        scoutId: scoutlist_id || undefined,
        messageId: message_id || undefined,
        recordScoutId: record_scoutlist_id || undefined,
        recordMessageId: record_message_id || undefined,
      },
    });
  };
}
